import React from 'react'

// Lazy load ALL pages for optimal code splitting
// This ensures the landing page bundle stays under 100KB (Requirement 1.4)

// Auth pages - loaded on demand
const SignInPage = React.lazy(() => import('@/pages/auth/SignInPage'))
const SignUpPage = React.lazy(() => import('@/pages/auth/SignUpPage'))
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage'))

// Landing page - lazy loaded with preload hint
const LandingPage = React.lazy(() => import('@/pages/LandingPage'))

// Lazy load non-critical pages
const StudentDashboard = React.lazy(() => import('@/pages/student/Dashboard'))
const AuthCallbackPage = React.lazy(() => import('@/pages/auth/AuthCallbackPage'))
const ApplicationWizard = React.lazy(() => import('@/pages/student/applicationWizard/index.tsx'))
const ApplicationStatus = React.lazy(() => import('@/pages/student/ApplicationStatus'))
const ApplicationDetail = React.lazy(() => import('@/pages/student/ApplicationDetail'))
const StudentSettings = React.lazy(() => import('@/pages/student/Settings'))
const StudentNotificationSettings = React.lazy(() => import('@/pages/student/NotificationSettings'))
const StudentPayment = React.lazy(() => import('@/pages/student/Payment'))
const StudentInterview = React.lazy(() => import('@/pages/student/Interview'))
const AdminDashboard = React.lazy(() => import('@/pages/admin/Dashboard'))
const AdminApplications = React.lazy(() => import('@/pages/admin/Applications'))
const ApplicationsAdmin = React.lazy(() => import('@/pages/admin/ApplicationsAdmin'))
const AdminPrograms = React.lazy(() => import('@/pages/admin/Programs'))
const AdminIntakes = React.lazy(() => import('@/pages/admin/Intakes'))
const AdminUsers = React.lazy(() => import('@/pages/admin/Users'))
const AdminSettings = React.lazy(() => import('@/pages/admin/Settings'))
const AdminAnalytics = React.lazy(() => import('@/pages/admin/Analytics'))
const AIInsights = React.lazy(() => import('@/pages/admin/AIInsights'))
const WorkflowAutomation = React.lazy(() => import('@/pages/admin/WorkflowAutomation'))
const AuditTrail = React.lazy(() => import('@/pages/admin/AuditTrail'))
const RoleManagement = React.lazy(() => import('@/pages/admin/RoleManagement'))
const ApplicationFlowAnalysis = React.lazy(() => import('@/pages/admin/ApplicationFlowAnalysis'))
const SystemHealthDashboard = React.lazy(() => import('@/pages/admin/SystemHealthDashboard'))
const PredictiveAnalytics = React.lazy(() => import('@/pages/admin/PredictiveAnalytics'))
const ComplianceAnalytics = React.lazy(() => import('@/pages/admin/ComplianceAnalytics'))
const RealtimeMetrics = React.lazy(() => import('@/pages/admin/RealtimeMetrics'))
const PublicApplicationTracker = React.lazy(() => import('@/pages/public/tracker/index.tsx'))
const AdminTest = React.lazy(() => import('@/pages/AdminTest'))
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'))

// Special components that don't need lazy loading
import { DashboardRedirect } from '@/components/DashboardRedirect'
import { Navigate } from 'react-router-dom'

export type RouteGuard = 'public' | 'auth' | 'student' | 'admin'

export interface RouteConfig {
  path: string
  element: React.ComponentType | React.ReactElement
  guard: RouteGuard
  lazy?: boolean
}

export const routes: RouteConfig[] = [
  // Public routes - all lazy loaded for optimal bundle size
  { path: '/', element: LandingPage, guard: 'public', lazy: true },
  { path: '/track-application', element: PublicApplicationTracker, guard: 'public', lazy: true },
  { path: '/auth/signin', element: SignInPage, guard: 'public', lazy: true },
  { path: '/signin', element: SignInPage, guard: 'public', lazy: true },
  { path: '/login', element: SignInPage, guard: 'public', lazy: true },
  { path: '/auth/signup', element: SignUpPage, guard: 'public', lazy: true },
  { path: '/auth/forgot-password', element: ForgotPasswordPage, guard: 'public', lazy: true },
  { path: '/auth/reset-password', element: ResetPasswordPage, guard: 'public', lazy: true },
  { path: '/auth/callback', element: AuthCallbackPage, guard: 'public', lazy: true },
  
  // Dashboard redirect (no lazy loading needed)
  { path: '/dashboard', element: <DashboardRedirect />, guard: 'public' },
  
  // Student routes
  { path: '/student/dashboard', element: StudentDashboard, guard: 'student', lazy: true },
  { path: '/apply', element: ApplicationWizard, guard: 'student', lazy: true },
  { path: '/student/application-wizard', element: ApplicationWizard, guard: 'student', lazy: true },
  { path: '/student/status', element: ApplicationStatus, guard: 'student', lazy: true },
  { path: '/application/:id', element: ApplicationStatus, guard: 'student', lazy: true },
  { path: '/student/application/:id', element: ApplicationDetail, guard: 'student', lazy: true },
  { path: '/settings', element: StudentSettings, guard: 'student', lazy: true },
  { path: '/student/profile', element: StudentSettings, guard: 'student', lazy: true },
  { path: '/student/settings', element: StudentSettings, guard: 'student', lazy: true },
  { path: '/student/notifications', element: StudentNotificationSettings, guard: 'student', lazy: true },
  { path: '/student/payment', element: StudentPayment, guard: 'student', lazy: true },
  { path: '/student/interview', element: StudentInterview, guard: 'student', lazy: true },
  
  // Admin routes
  { path: '/admin', element: AdminDashboard, guard: 'admin', lazy: true },
  { path: '/admin/dashboard', element: AdminDashboard, guard: 'admin', lazy: true },
  { path: '/admin/profile', element: AdminSettings, guard: 'admin', lazy: true },
  { path: '/admin-test', element: AdminTest, guard: 'auth', lazy: true },
  { path: '/admin/applications', element: AdminApplications, guard: 'admin', lazy: true },
  { path: '/admin/applications-new', element: ApplicationsAdmin, guard: 'admin', lazy: true },
  { path: '/admin/programs', element: AdminPrograms, guard: 'admin', lazy: true },
  { path: '/admin/intakes', element: AdminIntakes, guard: 'admin', lazy: true },
  { path: '/admin/users', element: AdminUsers, guard: 'admin', lazy: true },
  { path: '/admin/audit', element: AuditTrail, guard: 'admin', lazy: true },
  { path: '/admin/settings', element: AdminSettings, guard: 'admin', lazy: true },
  { path: '/admin/analytics', element: AdminAnalytics, guard: 'admin', lazy: true },
  { path: '/admin/predictive-analytics', element: PredictiveAnalytics, guard: 'admin', lazy: true },
  { path: '/admin/compliance-analytics', element: ComplianceAnalytics, guard: 'admin', lazy: true },
  { path: '/admin/realtime-metrics', element: RealtimeMetrics, guard: 'admin', lazy: true },
  { path: '/admin/ai-insights', element: AIInsights, guard: 'admin', lazy: true },
  { path: '/admin/workflow', element: WorkflowAutomation, guard: 'admin', lazy: true },
  { path: '/admin/flow-analysis', element: ApplicationFlowAnalysis, guard: 'admin', lazy: true },
  { path: '/admin/roles', element: RoleManagement, guard: 'admin', lazy: true },
  { path: '/admin/system-health', element: SystemHealthDashboard, guard: 'admin', lazy: true },
  
  // 404 routes
  { path: '/404', element: NotFoundPage, guard: 'public', lazy: true },
  { path: '*', element: <Navigate to="/404" replace />, guard: 'public' },
]